/**
 * Copyright 2015-present Singlepoint. All Rights Reserved.
 *
 * @flow
 */

import './SearchAllQuotes.scss';
import '../../styles/commonPageStyles.scss';

import { BOIGroupLogo } from '@boi/core/lib';
import { format } from 'date-fns';
import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { primaryBlue } from '../../constants/colors';
import { ERROR_TEXT_LATEST_QUOTE_BRANCH, ERROR_TEXT_LATEST_QUOTE_MORTGAGE } from '../../constants/commonConstants';
import {
  AGENT_CLICKED_REFERENCE_QUOTES,
  AGENT_CLICKED_REFERENCE_QUOTES_POLICY_CODE
} from '../../constants/sessionStorage/userStorageIdentifiers';
import { commonClass, saveInSessionStorage, withPrefix } from '../../helpers';
import { routes } from '../../routes';
import {
  getLatestHomeQuotesByType
} from '../../services/common/commonServices';
import type { ApiError } from '../../types/commonTypes';
import type { LatestUserQuoteSearchResponseModel } from '../../types/Home';
import AnimatedSpinner from '../AnimatedSpinner/AnimatedSpinner';
import { SearchForm } from './SearchForm';
import { SearchResults } from './SearchResults';

const SEARCH_TYPES = { branch: 'BRANCH', mortgage: 'MORTGAGE', email: 'EMAIL' };

function getSearchType(type: string): string {
  return SEARCH_TYPES[type] || '';
}

function getResponseErrorString(search_type: string): string {
  switch (search_type){
    case (SEARCH_TYPES.branch):
    case (SEARCH_TYPES.email):
      return ERROR_TEXT_LATEST_QUOTE_BRANCH;
    case (SEARCH_TYPES.mortgage):
      return ERROR_TEXT_LATEST_QUOTE_MORTGAGE;
    default:
      return '';
  }
}

// eslint-disable-next-line complexity
const SearchAllQuotes = () => {
  const [responseError, setResponseError] = useState(undefined);
  const [loadingResults, setLoadingResults] = useState(false);
  const [loadingType, setLoadingType] = useState('users');
  const [results, setResults] = useState<LatestUserQuoteSearchResponseModel[]>(
    []
  );

  const history = useHistory();

  const className = 'c-SearchAllQuotes';
  const withClass = withPrefix(className);

  // eslint-disable-next-line flowtype/require-parameter-type
  const handleURLClick = useCallback(async (email, policyCode) => {
    saveInSessionStorage(AGENT_CLICKED_REFERENCE_QUOTES, email);
    saveInSessionStorage(AGENT_CLICKED_REFERENCE_QUOTES_POLICY_CODE, policyCode);
    history.push(routes.quoteSummaryPage.url);
  }, []);

  const formatDate = (value: string, formatString: string) => {
    const dateTime = new Date(value);
    return format(dateTime, formatString);
  };

  // eslint-disable-next-line complexity
  const requestLatestHomeQuotesByType = async (
    value: string,
    type: string
  ): Promise<LatestUserQuoteSearchResponseModel[]> => {

    const search_criteria = value;
    const search_type = getSearchType(type);
    let page_number = 0;
    const pageSize = 1000;
    let arrData = [];
    let continueLoop = true;

    while (continueLoop) {
      const responseData = await getLatestHomeQuotesByType(
        search_criteria,
        page_number,
        pageSize,
        search_type
      );

      if (
        responseData === undefined ||
        responseData?.length === 0 ||
        responseData?.error
      ) {
        setResponseError(getResponseErrorString(search_type));
        continueLoop = false;
        return [];
      }
      arrData.push(...responseData.content);
      page_number++;
      if (page_number >= responseData.total_pages) {
        continueLoop = false;
      }
    }
    setResponseError(false);
    return arrData;
  };

  const processDate = (item: LatestUserQuoteSearchResponseModel) => ({
    ...item,
    date_of_birth: formatDate(item.date_of_birth, 'DD-MM-YYYY'),
    quote_timestamp: formatDate(item.quote_timestamp, 'DD-MM-YYYY HH:mm')
  });


  const fetcher = async (value: string, searchType: string) => {
    return await requestLatestHomeQuotesByType(value, searchType);
  };

  /* eslint-disable complexity */
  const fetchQuotes = async (searchType: string, value: string) => {
    setLoadingResults(true);
    setLoadingType(searchType === 'email' ? 'users' : 'quotes');
    const quotes: LatestUserQuoteSearchResponseModel[] | ApiError = await fetcher(
      value, searchType
    );

    setLoadingResults(false);
    if (!quotes.error) {
      // $FlowFixMe
      setResults(quotes.map(processDate));
    }
  };


  const onLoading = (isLoading: boolean) => {
    setResults([]);
    setLoadingResults(isLoading);
  };

  return (
    <div className={className}>
      <div className={withClass('__container')}>
        <div className={withClass('__innerContent')}>
          <div className={commonClass('__logo--flex')}>
            <BOIGroupLogo color={primaryBlue} />
          </div>
          <SearchForm
            fetchQuotes={fetchQuotes}
            setLoading={onLoading}
            setParentError={setResponseError}
          />
        </div>
      </div>
      {loadingResults ? (
        <div>
          <h1 className="search_text">
            Searching for {loadingType} matching your query
          </h1>
          <AnimatedSpinner size="large" />
        </div>
      ) : responseError ? (
        <div className={withClass('__responseError')}>
          <pre>{responseError}</pre>
        </div>
      ) : results.length > 0 ? (
        // eslint-disable-next-line no-unused-vars,flowtype/require-parameter-type
        <SearchResults results={results} onSelectQuote={(email, policyCode, e?: SyntheticEvent<>) => {
          handleURLClick(email, policyCode);
        }} />
      ) : undefined}
    </div>
  );
};

export default SearchAllQuotes;
